import React, { useEffect, useState, useCallback, RefObject } from 'react';
import classNames from 'classnames';

import ListRows from 'components/common/ListRows';
import RectangleRow from 'components/pages/home/RectangleRow';
import CircleRow from 'components/pages/home/CircleRow';

import focusManager from 'support/focus/FocusManager';
import { isPromoRow, isRowWithScoring } from 'support/rowHelper';
import { getHighlightsRowData } from 'support/scoringHelper';
import { useIntl } from 'react-intl';

import { useScoring } from 'hooks/scoring';
import HomeRow from './HomeRow';

import styles from './InteriorRows.module.scss';
import { KitchenSinkRowItem, RelatedItem, TileItem } from 'store-types';

const messages = {
  ttsEndOfNavigation: {
    id: 'navigation.tts.ttsEndOfNavigation',
    description: 'TTS text for navigation command cannot be executed',
  },
};

// pageName constants
const SPORTS = 'sports';
const LEAGUES = 'leagues';
const MARKETS = 'markets';
const SHOWS = 'shows';

interface TileClickProps {
  rowId: string;
  video?: TileItem | null;
  rowSlug?: string;
}

interface InteriorRowsProps {
  focusId?: string | number | null;
  onTileClick?: (props: TileClickProps) => void;
  renderBeforeFirst?: () => React.ReactNode;
  renderAfterLast?: () => React.ReactNode;
  onInteriorRowsFocusIndexChanged?: (index: number) => void;

  firstRowRef?: RefObject<unknown> | null;
  rowData?: KitchenSinkRowItem[] | null;
  relatedLeagues?: RelatedItem[] | null;
  relatedShows?: RelatedItem[] | null;
  relatedMarkets?: RelatedItem[] | null;
  pageName?: string | null;
  // Additional props passed but not used internally
  trackingRowOffset?: number | null;
  imageSrc?: string | unknown | null;
  title?: string | null;
  description?: string | null;
  rowsRef?: React.Ref<unknown> | null;
}

const InteriorRows: React.FC<InteriorRowsProps> = ({
  focusId: focusIdProp = null,
  onTileClick = () => {},
  renderBeforeFirst = () => null,
  renderAfterLast = () => null,
  onInteriorRowsFocusIndexChanged = () => {},
  firstRowRef = null,
  rowData = null,
  relatedLeagues = null,
  relatedShows = null,
  relatedMarkets = null,
  pageName = null,
  rowsRef = null,
}) => {
  const [focusId, setFocusId] = useState<string | number | null>(focusIdProp);

  useEffect(() => {
    if (focusIdProp) {
      setFocusId(focusIdProp);
    } else {
      setFocusId(focusManager.getNewUniqueId());
    }
  }, [focusIdProp]);

  let rows: KitchenSinkRowItem[] | undefined;
  if (pageName === SPORTS) {
    rows = [
      {
        id: 'sportspage-leagues',
        type: 'leagues',
        title: 'Leagues',
        url: '',
        data: relatedLeagues ?? null,
      },
      ...(rowData ?? []),
    ];
  } else if (pageName === LEAGUES) {
    rows = [
      ...(rowData ?? []),
      {
        id: 'leaguespage-leagues',
        type: 'leagues',
        title: 'Leagues',
        url: '',
        data: relatedLeagues ?? null,
      },
    ];
  } else if (pageName === SHOWS) {
    rows = [
      ...(rowData ?? []),
      {
        id: 'showspage-shows',
        type: 'shows',
        title: 'Shows',
        url: '',
        data: relatedShows ?? null,
      },
    ];
  } else if (pageName === MARKETS) {
    rows = [
      ...(rowData ?? []),
      {
        id: 'marketspage-markets',
        type: 'markets',
        title: 'Markets',
        url: '',
        data: relatedMarkets ?? null,
      },
    ];
  }

  const [games, scoringError] = useScoring();

  const renderRow = useCallback(
    (row, rowIndex) => {
      if (!row.data?.length) return null;

      const isPromo = isPromoRow(row);

      let focusIdPrefix: string | undefined;
      if (rowIndex === 0) {
        focusIdPrefix = 'firstrow-';
      } else if (rowIndex === (rows?.length as number) - 1) {
        focusIdPrefix = 'lastrow-';
      }

      let displayScoring = false;

      let updatedRow: KitchenSinkRowItem | null = null;
      if (row && isRowWithScoring(row) && !scoringError && games) {
        const highlightsRowData = getHighlightsRowData(row, games);
        if (highlightsRowData.length > 0) {
          displayScoring = true;
          updatedRow = { ...row, data: highlightsRowData };
        }
      }
      updatedRow = updatedRow || row;

      const getHomeRow = () => {
        if (row.id === 'sportspage-leagues' || row.id === 'leaguespage-leagues') {
          return (
            <RectangleRow
              className={styles.row}
              focusIdPrefix={focusIdPrefix}
              displayTitle
              title={row.id === 'sportspage-leagues' ? 'Leagues' : 'Related Leagues'}
              row={row}
              titleClassName={styles.rectangleRowTitle}
              rowClassName={classNames(styles.row, styles.inverseMargin)}
              rowPos={rowIndex + 1}
            />
          );
        }

        if (row.id === 'showspage-shows' || row.id === 'marketspage-markets') {
          return (
            <CircleRow
              className={styles.row}
              focusIdPrefix={focusIdPrefix}
              displayTitle
              title={row.id === 'showspage-shows' ? 'Shows' : 'Local Markets'}
              row={row}
              titleClassName={styles.circleRowTitle}
              rowClassName={classNames(styles.row, styles.inverseMargin)}
              rowPos={rowIndex + 1}
            />
          );
        }

        return (
          <HomeRow
            row={updatedRow!}
            displayTitle
            displayScoring={displayScoring}
            games={games}
            onTileClick={onTileClick}
            key={updatedRow?.id}
            ref={rowIndex === 0 ? firstRowRef : null}
            rowClassName={classNames(styles.row, styles.inverseMargin, {
              [styles.promoRow]: isPromo,
            })}
            titleClassName={classNames(styles.rowTitle, {
              [styles.highlightsRowTitle]: displayScoring,
            })}
            tileClassName={styles.tile}
            focusIdPrefix={focusIdPrefix}
            rowPos={rowIndex + 1}
          />
        );
      };

      let firstRowStyle: string;
      if (pageName === SPORTS) {
        firstRowStyle = styles.firstRowShort;
      } else {
        firstRowStyle = styles.firstRowTall;
      }

      if (rowIndex === 0) {
        return (
          <div className={firstRowStyle}>
            <div className="flex-grow-1" />
            <div className={styles.beforeFirst}>{renderBeforeFirst?.()}</div>
            {getHomeRow()}
            {(rows?.length as number) === 1 && renderAfterLast?.()}
          </div>
        );
      }

      if (
        rowIndex === (rows?.length as number) - 1 ||
        (rows?.[(rows?.length as number) - 1]?.data === null && rowIndex === rows.length - 2)
      ) {
        return (
          <>
            {getHomeRow()}
            {renderAfterLast?.()}
          </>
        );
      }
      return getHomeRow();
    },
    [
      firstRowRef,
      onTileClick,
      games,
      renderAfterLast,
      renderBeforeFirst,
      rows,
      scoringError,
      pageName,
    ],
  );

  const shouldSnapRowToTop = useCallback(() => true, []);

  const onListRowsFocusIndexChanged = useCallback(
    (index: number) => {
      onInteriorRowsFocusIndexChanged?.(index);
    },
    [onInteriorRowsFocusIndexChanged],
  );

  const ttsEndOfNavigation = useIntl().formatMessage(messages.ttsEndOfNavigation);

  return (
    <>
      <ListRows
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={rowsRef as React.Ref<any>}
        focusId={focusId}
        className={styles.listRows}
        data={rows || []}
        renderItem={renderRow}
        buffer={2}
        restorationId={`${focusId}`}
        onListRowsFocusIndexChanged={onListRowsFocusIndexChanged}
        shouldSnapRowToTop={shouldSnapRowToTop}
        ttsEndOfNavigation={ttsEndOfNavigation}
      />
    </>
  );
};

export default React.forwardRef<unknown, Omit<InteriorRowsProps, 'rowsRef'>>((props, ref) => (
  <InteriorRows {...props} rowsRef={ref} />
));
